import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import withStyles from 'react-jss';
import { Button, Select, Row, Input } from 'antd';
import { useHistory } from 'react-router';
import { useForm, Controller } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';

import GetField from '../Components/GetField';
import styles from '../styles';
import { successNotification, errorNotification } from '../Snackbar';
import Api from '../Api';
import LeftGrid from '../Components/LeftGrid';
import RightGrid from '../Components/RightGrid';
import {
  getCustomers,
  clearCustomers,
  clearProductIds,
  getProductIds,
  getFactoryIds,
  clearFactoryIds,
  getRackIds,
  clearRackIds,
  getFabricsForJobchart,
  clearFabricsForJobchart,
  getColorIds,
  clearColorIds,
  getSingleFabricInfo,
  clearSingleFabricInfo,
  clearActiveJobchartIds,
  getActiveJobchartIds,
} from '../Actions';
import Breadcrumbs from '../Components/Breadcrumbs';

const AntInput = GetField(Input);
const AntSelect = GetField(Select);
const { Option } = Select;
const AntInputTextArea = GetField(Input.TextArea);

const UpdateFabricInInventory = props => {
  const { classes, match } = props;
  const history = useHistory();
  const dispatch = useDispatch();

  const { handleSubmit, control, reset, getValues, watch, setValue } =
    useForm();

  const [confirmLoading, setConfirmLoading] = useState(false);

  const customers = useSelector(state => state.customers);
  const productIds = useSelector(state => state.productIds);
  const activeJobchartIds = useSelector(state => state.activeJobchartIds);
  const factoryIds = useSelector(state => state.factoryIds);
  const rackIds = useSelector(state => state.rackIds);
  const colorIds = useSelector(state => state.colorIds);
  const fabricsForJobchart = useSelector(state => state.fabricsForJobchart);
  const fabricInfo = useSelector(state => state.fabricInfo);

  useEffect(() => {
    dispatch(getCustomers());
    dispatch(getFactoryIds());
    dispatch(getRackIds());
    dispatch(getColorIds());
    dispatch(getFabricsForJobchart());
    dispatch(getSingleFabricInfo(match.params.id));
    return () => {
      dispatch(clearCustomers());
      dispatch(clearFactoryIds());
      dispatch(clearRackIds());
      dispatch(clearColorIds());
      dispatch(clearFabricsForJobchart());
      dispatch(clearSingleFabricInfo());
    };
  }, []);

  useEffect(() => {
    reset(fabricInfo);
  }, [fabricInfo]);

  useEffect(() => {
    const customerId = getValues('customerId');
    if (!customerId) {
      dispatch(clearProductIds());
      setValue('productId', null);
    } else {
      dispatch(getProductIds(customerId));
    }
  }, [watch('customerId')]);

  useEffect(() => {
    const productId = getValues('productId');
    if (!productId) {
      dispatch(clearActiveJobchartIds());
      setValue('jobchartId', null);
    } else {
      dispatch(getActiveJobchartIds({ productId }));
    }
  }, [watch('productId')]);

  const fabricWidths = useMemo(() => {
    const fabric = getValues('fabric');
    if (!fabric) {
      return [];
    }
    return fabricsForJobchart.widths[fabric] || [];
  }, [watch('fabric')]);

  const onSubmit = useCallback(async formValues => {
    try {
      setConfirmLoading(true);
      await Api.put(`/fabric-inventory/${match.params.id}`, formValues);
      successNotification('Fabric updated');
      history.push('/inventory/fabric');
    } catch (err) {
      setConfirmLoading(false);
      errorNotification(err);
    }
  }, []);

  return (
    <>
      <Breadcrumbs
        moduleName="Fabric Inventory"
        moduleLink="/inventory/fabric"
        subModuleName="Update"
      />
      <form onSubmit={handleSubmit(onSubmit)}>
        <Row>
          <LeftGrid>
            <Controller
              name="customerId"
              control={control}
              render={({ field }) => (
                <AntSelect
                  label="Customer"
                  placeholder="Select customer"
                  {...field}
                >
                  {customers.map(customer => (
                    <Option key={customer.id} value={customer.id}>
                      {customer.name}
                    </Option>
                  ))}
                </AntSelect>
              )}
            />
          </LeftGrid>
          <RightGrid>
            <Controller
              name="productId"
              control={control}
              render={({ field }) => (
                <AntSelect
                  label="Product"
                  allowClear
                  placeholder="Select product"
                  {...field}
                >
                  {productIds.map(product => (
                    <Option key={product.id} value={product.id}>
                      {product.name}
                    </Option>
                  ))}
                </AntSelect>
              )}
            />
          </RightGrid>
        </Row>
        <Row>
          <LeftGrid>
            <Controller
              name="jobchartId"
              control={control}
              render={({ field }) => (
                <AntSelect
                  label="BPO #"
                  allowClear
                  placeholder="Select bpo"
                  {...field}
                >
                  {activeJobchartIds.map(jobchart => (
                    <Option key={jobchart.id} value={jobchart.id}>
                      {jobchart.bpoNo}
                    </Option>
                  ))}
                </AntSelect>
              )}
            />
          </LeftGrid>
          <RightGrid>
            <Controller
              name="fabric"
              control={control}
              render={({ field }) => (
                <AntSelect
                  allowClear
                  label="Fabric"
                  placeholder="Select fabric"
                  {...field}
                >
                  {fabricsForJobchart.fabricNames.map(fabric => (
                    <Option key={fabric} value={fabric}>
                      {fabric}
                    </Option>
                  ))}
                </AntSelect>
              )}
            />
          </RightGrid>
        </Row>
        <Row>
          <LeftGrid>
            <Controller
              name="fabricId"
              control={control}
              render={({ field }) => (
                <AntSelect
                  allowClear
                  label="Width"
                  placeholder="Select width"
                  {...field}
                >
                  {fabricWidths.map(fabric => (
                    <Option key={fabric.id} value={fabric.id}>
                      {fabric.width}
                    </Option>
                  ))}
                </AntSelect>
              )}
            />
          </LeftGrid>
          <RightGrid>
            <Controller
              name="baseColorId"
              control={control}
              render={({ field }) => (
                <AntSelect
                  allowClear
                  label="Base Color"
                  placeholder="Select base color"
                  {...field}
                >
                  {colorIds.map(color => (
                    <Option key={color.id} value={color.id}>
                      {color.color}
                    </Option>
                  ))}
                </AntSelect>
              )}
            />
          </RightGrid>
        </Row>
        <Row>
          <LeftGrid>
            <Controller
              name="printColorId"
              control={control}
              render={({ field }) => (
                <AntSelect
                  allowClear
                  label="Print Color"
                  placeholder="Select print color"
                  {...field}
                >
                  {colorIds.map(color => (
                    <Option key={color.id} value={color.id}>
                      {color.color}
                    </Option>
                  ))}
                </AntSelect>
              )}
            />
          </LeftGrid>
          <RightGrid>
            <Controller
              name="factoryId"
              control={control}
              render={({ field }) => (
                <AntSelect
                  allowClear
                  label="Factory"
                  placeholder="Select factory"
                  {...field}
                >
                  {factoryIds.map(factory => (
                    <Option key={factory.id} value={factory.id}>
                      {factory.name}
                    </Option>
                  ))}
                </AntSelect>
              )}
            />
          </RightGrid>
        </Row>
        <Row>
          <LeftGrid>
            <Controller
              name="racknoId"
              control={control}
              render={({ field }) => (
                <AntSelect
                  allowClear
                  label="Rack #"
                  placeholder="Select rack"
                  {...field}
                >
                  {rackIds.map(rack => (
                    <Option key={rack.id} value={rack.id}>
                      {rack.rackno}
                    </Option>
                  ))}
                </AntSelect>
              )}
            />
          </LeftGrid>
          <RightGrid>
            <Controller
              name="quantity"
              control={control}
              render={({ field }) => (
                <AntInput
                  allowClear
                  type="number"
                  min={0}
                  step="any"
                  label="Quantity"
                  placeholder="Quantity"
                  {...field}
                />
              )}
            />
          </RightGrid>
        </Row>
        <Row>
          <LeftGrid>
            <Controller
              name="remark"
              control={control}
              render={({ field }) => (
                <AntInputTextArea
                  allowClear
                  label="Remark"
                  placeholder="Remark"
                  {...field}
                />
              )}
            />
          </LeftGrid>
        </Row>
        <Row className={classes.buttonContainer}>
          <Button type="primary" loading={confirmLoading} htmlType="submit">
            Submit
          </Button>
        </Row>
      </form>
    </>
  );
};

UpdateFabricInInventory.propTypes = {
  classes: PropTypes.object.isRequired,
  match: PropTypes.object.isRequired,
};

export default withStyles(styles)(UpdateFabricInInventory);
